import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

import { QuotationPdfService } from "components/Report/QuotationTransaction/pdf/QuotationPdfService";
import { translator } from "../utils/Translator";

// Configure pdf.js worker (Webpack 4 / CRA 4 friendly)
try {
  if (pdfjsWorker) {
    GlobalWorkerOptions.workerSrc = pdfjsWorker;
  }
} catch (_) {
  // ignore
}

const isAndroidWebViewUserAgent = () => {
  try {
    const ua = String(navigator?.userAgent || "");
    const isAndroid = /Android/i.test(ua);
    if (!isAndroid) return false;

    // Common Android WebView signals
    const hasWv = /\bwv\b/i.test(ua);
    const hasVersionToken = /Version\//i.test(ua);
    const isRN = typeof window?.ReactNativeWebView?.postMessage === "function";
    return hasWv || hasVersionToken || isRN;
  } catch (_) {
    return false;
  }
};

const PdfCanvasViewer = ({ blob }) => {
  const containerRef = useRef(null);
  const [viewerState, setViewerState] = useState({ status: "idle", error: "" });

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      if (!blob) return;
      const container = containerRef.current;
      if (!container) return;

      setViewerState({ status: "loading", error: "" });
      container.innerHTML = "";

      const readAsArrayBuffer = async (b) => {
        if (!b) return new ArrayBuffer(0);
        if (typeof b.arrayBuffer === "function") return await b.arrayBuffer();
        return await new Promise((resolve, reject) => {
          try {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("FileReader failed"));
            reader.readAsArrayBuffer(b);
          } catch (e) {
            reject(e);
          }
        });
      };

      const buf = await readAsArrayBuffer(blob);
      const data = new Uint8Array(buf);
      const loadingTask = getDocument({ data });
      const pdf = await loadingTask.promise;

      const desiredWidth = Math.max(320, container.clientWidth || window.innerWidth || 320);
      const dpr = Math.max(1, Number(window.devicePixelRatio || 1));

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        if (cancelled) break;
        const page = await pdf.getPage(pageNumber);
        const viewport1x = page.getViewport({ scale: 1 });
        const scale = Math.max(0.25, desiredWidth / viewport1x.width);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.style.display = "block";
        canvas.style.margin = "0 auto 12px";
        canvas.style.background = "#fff";
        canvas.style.boxShadow = "0 1px 6px rgba(0,0,0,0.08)";
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);

        const ctx = canvas.getContext("2d", { alpha: false });
        const renderTask = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        });

        container.appendChild(canvas);
        await renderTask.promise;
      }

      try {
        await pdf.cleanup();
      } catch (_) {}

      if (!cancelled) setViewerState({ status: "done", error: "" });
    };

    render().catch((e) => {
      if (cancelled) return;
      setViewerState({ status: "error", error: e?.message || "PDF render failed" });
    });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  return (
    <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", background: "#f5f5f5" }}>
      {viewerState.status === "loading" && (
        <div style={{ padding: 12, color: "#666", fontFamily: "sans-serif", fontSize: 12 }}>
          กำลังแสดงผล PDF...
        </div>
      )}
      {viewerState.status === "error" && (
        <div style={{ padding: 12, color: "#b00020", fontFamily: "sans-serif", fontSize: 12 }}>
          ไม่สามารถแสดง PDF ใน WebView ได้: {viewerState.error}
        </div>
      )}
      <div ref={containerRef} style={{ padding: 12 }} />
    </div>
  );
};

function useQueryParams() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const get = (key, fallback = "") => {
      const v = params.get(key);
      return v === null || v === undefined ? fallback : v;
    };

    // Support both docNumber and docNo (common external naming)
    const docNumber = get("docNumber") || get("docNo") || get("documentNo") || "";
    const company = get("company") || "";

    // language: 1/2/3... (backend expects number)
    const languageRaw = get("language") || get("lang") || "";
    const language = Number(languageRaw || 1) || 1;

    const normalizeToken = (t) => {
      let s = String(t || "").trim();
      if (!s) return "";
      s = s.replace(/^Bearer\s+/i, "").trim();
      // Allow common wrapper mistakes: <token> or "token" or 'token'
      if ((s.startsWith("<") && s.endsWith(">")) || (s.startsWith("\"") && s.endsWith("\"")) || (s.startsWith("'") && s.endsWith("'"))) {
        s = s.slice(1, -1).trim();
      }
      return s;
    };
    const token = normalizeToken(
      get("token") ||
        get("accessToken") ||
        get("shareToken") ||
        get("authorization") ||
        get("auth") ||
        ""
    );
    const pdfType = get("pdfType") || "with_date";

    // Optional: allow selecting header mapping
    const docHeaderTypeCode = get("docHeaderTypeCode") || get("headerType") || "";
    const docHeaderImageFilename = get("docHeaderImageFilename") || get("headerImage") || "";
    const docHeaderImageFileLocation =
      get("docHeaderImageFileLocation") || get("headerImageLocation") || "";

    // mode=open (new tab/blob) | download (pdf.save)
    const mode = (get("mode") || "").toLowerCase() || "open";

    const salesmanCode = get("salesmanCode") || "";

    return {
      docNumber,
      company,
      language,
      token,
      pdfType,
      docHeaderTypeCode,
      docHeaderImageFilename,
      docHeaderImageFileLocation,
      mode,
      salesmanCode,
    };
  }, [location.search]);
}

const ShareQuotationPdf = () => {
  const translate = useSelector(({ i18n }) => i18n?.translate);

  const {
    docNumber,
    company,
    language,
    token,
    pdfType,
    docHeaderTypeCode,
    docHeaderImageFilename,
    docHeaderImageFileLocation,
    mode,
    salesmanCode,
  } = useQueryParams();

  const [status, setStatus] = useState("idle"); // idle|loading|done|error
  const [errorMessage, setErrorMessage] = useState("");
  const [authToken, setAuthToken] = useState(token || "");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [retrySeq, setRetrySeq] = useState(0);
  const lastRunKeyRef = useRef("");
  const inFlightRef = useRef(false);
  const lastOutcomeRef = useRef({ key: "", status: "idle" });
  const blockedRequestRef = useRef({ requestKey: "", retrySeq: 0 });
  const autoDownloadRef = useRef({ key: "", attempted: false });
  const pdfBlobRef = useRef(null);
  const swReadyRef = useRef(false);

  const getStoredToken = () => {
    try {
      return String(localStorage.getItem("x-access-token") || "").trim();
    } catch (_) {
      return "";
    }
  };

  // Keep query token as a fallback (not recommended for production).
  useEffect(() => {
    setAuthToken(token || "");
  }, [token]);

  // Cleanup PDF blob URL when it changes/unmounts
  useEffect(() => {
    return () => {
      try {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      } catch (_) {}
    };
  }, [pdfUrl]);

  // Register a minimal service worker used to serve generated PDFs via a normal http(s) URL.
  // This helps Android WebView download handlers that cannot download blob: URLs.
  useEffect(() => {
    let cancelled = false;

    const waitForController = (timeoutMs = 4000) =>
      new Promise((resolve) => {
        try {
          if (!("serviceWorker" in navigator)) return resolve(false);
          if (navigator.serviceWorker.controller) return resolve(true);

          const onChange = () => {
            cleanup();
            resolve(true);
          };
          const timer = setTimeout(() => {
            cleanup();
            resolve(!!navigator.serviceWorker.controller);
          }, timeoutMs);

          const cleanup = () => {
            clearTimeout(timer);
            navigator.serviceWorker.removeEventListener("controllerchange", onChange);
          };

          navigator.serviceWorker.addEventListener("controllerchange", onChange);
        } catch (_) {
          resolve(false);
        }
      });

    const register = async () => {
      try {
        if (!("serviceWorker" in navigator)) return;
        const publicUrl = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
        const swUrl = `${publicUrl}/share-pdf-sw.js`;
        await navigator.serviceWorker.register(swUrl, { scope: `${publicUrl}/` });

        const ready = await waitForController();
        if (cancelled) return;
        swReadyRef.current = !!ready;
      } catch (_) {
        // ignore
      }
    };

    register();
    return () => {
      cancelled = true;
    };
  }, []);

  const prepareHttpDownloadUrl = async (blob, filename) => {
    try {
      if (!blob) return "";
      if (!("serviceWorker" in navigator)) return "";
      if (!("caches" in window)) return "";

      // Ensure controller is ready (best-effort).
      if (!navigator.serviceWorker.controller) {
        await new Promise((resolve) => {
          const t = setTimeout(resolve, 1200);
          const onChange = () => {
            clearTimeout(t);
            navigator.serviceWorker.removeEventListener("controllerchange", onChange);
            resolve();
          };
          navigator.serviceWorker.addEventListener("controllerchange", onChange);
        });
      }
      if (!navigator.serviceWorker.controller) return "";

      const origin = window.location.origin;
      const pathname = window.location.pathname || "";
      const marker = "/share/quotation";
      const idx = pathname.indexOf(marker);
      const basePath = idx >= 0 ? pathname.slice(0, idx) : "";

      const safeFilename = String(filename || "Quotation.pdf").trim() || "Quotation.pdf";
      const encodedName = encodeURIComponent(safeFilename);
      const key = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
      const urlPath = `${basePath}/__share_pdf__/${encodedName}?k=${key}`;
      const fullUrl = `${origin}${urlPath}`;

      const headers = new Headers({
        "Content-Type": "application/pdf",
        "Content-Disposition": `attachment; filename="${safeFilename.replace(/\"/g, "")}"`,
        "Cache-Control": "no-store",
      });

      const cache = await caches.open("isa-share-pdf-cache-v1");
      await cache.put(fullUrl, new Response(blob, { status: 200, headers }));
      return fullUrl;
    } catch (_) {
      return "";
    }
  };

  // Safer for mobile: send token via postMessage instead of putting it in the URL.
  // Supported payloads:
  // - string token
  // - JSON string: { type: 'ISA_SHARE_AUTH', token: '...' }
  // - object: { type: 'ISA_SHARE_AUTH', token: '...' }
  useEffect(() => {
    const parseToken = (data) => {
      const normalizeToken = (t) => {
        let s = String(t || "").trim();
        if (!s) return "";
        s = s.replace(/^Bearer\s+/i, "").trim();
        if ((s.startsWith("<") && s.endsWith(">")) || (s.startsWith("\"") && s.endsWith("\"")) || (s.startsWith("'") && s.endsWith("'"))) {
          s = s.slice(1, -1).trim();
        }
        return s;
      };
      const looksLikeJwt = (t) => {
        const s = String(t || "").trim();
        if (!s) return false;
        // Common JWT patterns: starts with 'eyJ' or 3 base64url segments.
        if (s.startsWith("eyJ")) return true;
        return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(s);
      };

      const pickTokenFromObject = (obj) => {
        if (!obj || typeof obj !== "object") return "";
        const type = String(obj?.type || "").trim();
        const raw = obj?.token ?? obj?.accessToken ?? obj?.authorization ?? "";
        const t = normalizeToken(raw);
        if (!t) return "";

        // Prefer explicit type tag; otherwise accept only JWT-looking strings.
        if (type) return type === "ISA_SHARE_AUTH" ? t : "";
        return looksLikeJwt(t) ? t : "";
      };

      if (!data) return "";
      if (typeof data === "string") {
        const s = data.trim();
        if (!s) return "";
        // If JSON, parse it
        if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
          try {
            const obj = JSON.parse(s);
            const t = pickTokenFromObject(obj);
            if (t) return t;
          } catch (_) {
            // fall through
          }
        }
        // If raw token string
        const raw = normalizeToken(s);
        return looksLikeJwt(raw) ? raw : "";
      }

      if (typeof data === "object") {
        return pickTokenFromObject(data);
      }
      return "";
    };

    const isAllowedOrigin = (origin) => {
      try {
        // Some WebViews may provide origin = 'null'
        if (!origin || origin === "null") return true;
        return origin === window.location.origin;
      } catch (_) {
        return true;
      }
    };

    const handler = (event) => {
      if (event && event.origin && !isAllowedOrigin(event.origin)) return;
      const t = parseToken(event?.data);
      if (t) {
        setAuthToken((prev) => (prev === t ? prev : t));
      }
    };

    window.addEventListener("message", handler);

    // Android ReactNativeWebView sometimes emits on document.
    const docHandler = (event) => {
      const t = parseToken(event?.data);
      if (t) {
        setAuthToken((prev) => (prev === t ? prev : t));
      }
    };
    document.addEventListener("message", docHandler);

    return () => {
      window.removeEventListener("message", handler);
      document.removeEventListener("message", docHandler);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const requestKey = JSON.stringify({
        docNumber,
        company,
        language,
        pdfType,
        docHeaderTypeCode,
        docHeaderImageFilename,
        docHeaderImageFileLocation,
        mode,
        salesmanCode,
      });

      // Hard stop: if this request already failed, do not keep retrying automatically.
      // User must press "ลองใหม่".
      if (
        blockedRequestRef.current.requestKey === requestKey &&
        retrySeq <= blockedRequestRef.current.retrySeq
      ) {
        return;
      }

      setErrorMessage("");
      setPdfUrl("");
      setPdfFilename("");
      setPdfDownloadUrl("");
      pdfBlobRef.current = null;
      setPdfBlob(null);

      if (!docNumber || !company) {
        setStatus("error");
        setErrorMessage("กรุณาระบุพารามิเตอร์ company และ docNumber");
        blockedRequestRef.current = { requestKey, retrySeq };
        return;
      }

      // If the browser already has a session token, we can proceed without passing authToken.
      const storedToken = getStoredToken();
      const effectiveToken = String(authToken || "").trim();

      // if (!effectiveToken && !storedToken) {
      //   setStatus("idle");
      //   setErrorMessage("ต้องเข้าสู่ระบบก่อน หรือให้แอพส่ง token ผ่าน postMessage");
      //   // Do not block this case; once token arrives, it should run.
      //   return;
      // }

      const hashToken = (s) => {
        // Non-cryptographic hash for keying only (avoid embedding token in the key)
        let h = 5381;
        const str = String(s || "");
        for (let i = 0; i < str.length; i++) {
          h = (h * 33) ^ str.charCodeAt(i);
        }
        return (h >>> 0).toString(16);
      };

      // Prevent duplicate re-runs with the same inputs
      const runKey = JSON.stringify({
        docNumber,
        company,
        language,
        pdfType,
        docHeaderTypeCode,
        docHeaderImageFilename,
        docHeaderImageFileLocation,
        mode,
        salesmanCode,
        token: effectiveToken ? `t:${hashToken(effectiveToken)}` : storedToken ? "storage" : "no_token",
      });

      // If the same request is already running or already attempted, do not spam API.
      if (inFlightRef.current && lastRunKeyRef.current === runKey) return;
      if (
        lastOutcomeRef.current.key === runKey &&
        (lastOutcomeRef.current.status === "done" || lastOutcomeRef.current.status === "error")
      ) {
        return;
      }

      lastRunKeyRef.current = runKey;

      setStatus("loading");
      inFlightRef.current = true;
      try {
        const quotationData = {
          DocNumber: docNumber,
          Company: company,
          SalesmanCode: salesmanCode,
        };

        const options = {
          language,
          pdfType,
          docHeaderTypeCode: docHeaderTypeCode || undefined,
          docHeaderImageFilename: docHeaderImageFilename || undefined,
          docHeaderImageFileLocation: docHeaderImageFileLocation || undefined,
          // Prefer token received from postMessage (safer than query string)
          authorizationToken: effectiveToken || undefined,
          translate,
          translator,
        };

        // Always generate a Blob URL.
        // Why: mobile browsers/WebViews often block programmatic downloads (jsPDF.save)
        // and can look like a redirect. A visible "Download" button (user gesture)
        // is the most reliable.
        const res = await QuotationPdfService.fetchAndGeneratePdf(quotationData, {
          ...options,
          output: "blobUrl",
        });
        const url = String(res?.blobUrl || "").trim();
        const fn = String(res?.filename || "").trim();
        const blob = res?.blob || null;
        if (url) setPdfUrl(url);
        if (fn) setPdfFilename(fn);
        if (blob) pdfBlobRef.current = blob;
        if (blob) setPdfBlob(blob);

        // Build a normal http(s) download URL for Android WebView (non-blob).
        if (blob) {
          const httpUrl = await prepareHttpDownloadUrl(blob, fn || "Quotation.pdf");
          if (!cancelled && httpUrl) setPdfDownloadUrl(httpUrl);
        }

        if (cancelled) return;
        setStatus("done");
        lastOutcomeRef.current = { key: runKey, status: "done" };
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(e?.message || "เกิดข้อผิดพลาดในการสร้าง PDF");
        lastOutcomeRef.current = { key: runKey, status: "error" };
        // Block auto retries for the same request params.
        blockedRequestRef.current = { requestKey, retrySeq };
      } finally {
        inFlightRef.current = false;
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    docNumber,
    company,
    language,
    authToken,
    pdfType,
    docHeaderTypeCode,
    docHeaderImageFilename,
    docHeaderImageFileLocation,
    mode,
    salesmanCode,
    retrySeq,
  ]);

  const triggerDownload = async (url, filename, blob) => {
    const blobHref = String(url || "").trim();
    const httpHref = String(pdfDownloadUrl || "").trim();
    const href = httpHref || blobHref;
    if (!href) return;

    const safeName = String(filename || "Quotation.pdf").trim() || "Quotation.pdf";

    // iOS Safari/WebView often ignores `download` for blob URLs.
    // In that case, opening the blob in a new tab (or same tab) is more reliable.
    const ua = String(navigator?.userAgent || "");
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !/Macintosh/.test(ua);

    // If running inside ReactNative WebView, prefer passing the PDF bytes to native.
    // Why: Android WebView download listeners generally cannot download blob: URLs.
    const rnPostMessage = window?.ReactNativeWebView?.postMessage;
    if (typeof rnPostMessage === "function" && httpHref) {
      try {
        rnPostMessage(
          JSON.stringify({
            type: "ISA_DOWNLOAD_PDF_URL",
            filename: safeName,
            url: httpHref,
          })
        );
        return;
      } catch (_) {
        // fall through
      }
    }

    if (typeof rnPostMessage === "function" && blob) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ""));
          reader.onerror = () => reject(new Error("FileReader failed"));
          reader.readAsDataURL(blob);
        });
        rnPostMessage(
          JSON.stringify({
            type: "ISA_DOWNLOAD_PDF_BASE64",
            filename: safeName,
            dataUrl,
          })
        );
        return;
      } catch (_) {
        // fall through to browser download
      }
    }

    try {
      if (isIOS) {
        const win = window.open(href, "_blank", "noopener,noreferrer");
        if (!win) window.location.assign(href);
        return;
      }

      const a = document.createElement("a");
      a.href = href;
      a.download = safeName;
      a.rel = "noopener noreferrer";
      // For Android WebView download interceptors, _self is often more reliable.
      a.target = httpHref ? "_self" : "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (_) {
      try {
        window.open(href, "_blank", "noopener,noreferrer");
      } catch (_) {}
    }
  };

  // Attempt auto-download when coming from a share link with mode=download.
  // Keep a button as fallback (user gesture) for WebViews that block it.
  useEffect(() => {
    if (mode !== "download") return;
    if (status !== "done") return;
    if (!pdfUrl) return;

    const key = `${docNumber}|${company}|${language}|${pdfType}|${retrySeq}`;
    if (autoDownloadRef.current.key === key && autoDownloadRef.current.attempted) return;
    autoDownloadRef.current = { key, attempted: true };

    // Delay a tick so the UI has time to paint (some WebViews are picky).
    const t = setTimeout(() => {
      triggerDownload(pdfUrl, pdfFilename, pdfBlobRef.current);
    }, 250);
    return () => clearTimeout(t);
  }, [mode, status, pdfUrl, pdfFilename, docNumber, company, language, pdfType, retrySeq]);

// 1. ปรับสไตล์ให้ขยายเต็มพื้นที่จอ
  const containerStyle = {
    margin: 0,
    padding: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#fff",
  };

  const viewerStyle = {
    flex: 1, // ยืดให้เต็มพื้นที่ที่เหลือ
    width: "100%",
    height: "100%",
    border: "none",
  };

  const barStyle = {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    borderBottom: "1px solid #eee",
    fontFamily: "sans-serif",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  };

  const buttonStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #222",
    background: "#111",
    color: "#fff",
    cursor: "pointer",
  };

  return (
    <div style={containerStyle}>
      {status === "error" && (
        <div style={{ padding: 20, color: "#b00020", textAlign: "center" }}>
          <p>เกิดข้อผิดพลาด: {errorMessage}</p>
          <button onClick={() => setRetrySeq(n => n + 1)}>ลองใหม่</button>
        </div>
      )}

      {/* --- ส่วนแสดง PDF (โผล่มาในหน้าเดิม ไม่ Redirect) --- */}
      {status === "done" && pdfUrl && (
        <>
          <div style={barStyle}>
            <button
              type="button"
              style={buttonStyle}
              onClick={() => triggerDownload(pdfUrl, pdfFilename, pdfBlobRef.current)}
            >
              ดาวน์โหลด PDF
            </button>
            <div style={{ fontSize: 12, color: "#666", overflow: "hidden", textOverflow: "ellipsis" }}>
              {pdfFilename || "Quotation.pdf"}
              {mode === "download" && (
                <span style={{ marginLeft: 8 }}>
                  (ถ้าไม่ดาวน์โหลดอัตโนมัติ ให้กดปุ่มนี้)
                </span>
              )}
            </div>
          </div>

          {/* Android WebView มักเปิด/แสดง blob PDF ใน iframe ไม่ได้ (เห็นแถบขาวอย่างเดียว)
              จึง render ด้วย pdf.js เป็น canvas เพื่อให้ใช้งานได้จริง */}
          {isAndroidWebViewUserAgent() && pdfBlob ? (
            <PdfCanvasViewer blob={pdfBlob} />
          ) : (
            <iframe
              title="Quotation PDF"
              src={`${String(pdfDownloadUrl || pdfUrl)}#view=FitH`}
              style={viewerStyle}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ShareQuotationPdf;
